import React, { useEffect, useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { addDoc, collection, doc, getDoc, Timestamp } from "firebase/firestore";
import Swal from "sweetalert2";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    InputAdornment,
    IconButton,
    Fade
} from "@mui/material";
import { 
    Visibility, 
    VisibilityOff, 
    EmailOutlined,
    LockOutlined
} from "@mui/icons-material";

function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [systemDetails, setSystemDetails] = useState({});
    const [loading, setLoading] = useState(false);

    // Color Palette from WhatsApp Image 2026-08-16 at 6.51.07 AM.jpeg
    const colors = {
        darkGreen: '#06362D',
        calPolyGreen: '#155332',
        vanilla: '#DEE2B1',
        gold: '#FFD500',
        goldenrod: '#D1A505'
    };

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        background: colors.calPolyGreen,
        color: colors.vanilla,
    });

    useEffect(() => {
        setSystemDetails({
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
    }, []);

    const login1 = async () => {
        if (!email || !password) {
            return Toast.fire({
                icon: "error",
                title: "Please enter your email and password",
            });
        }

        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            const user = auth.currentUser;

            if (user) {
                const docRef = doc(db, "authroles", user.uid);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists() && docSnap.data().role === "General Manager") {
                    Toast.fire({ icon: "success", title: "Login Successful!" });

                    await addDoc(collection(db, "logindetails"), {
                        uid: user.uid,
                        username: docSnap.data().name,
                        systemDetails,
                        createdTime: Timestamp.now(),
                    });

                    window.location.href = "/home";
                } else {
                    Toast.fire({ icon: "error", title: "Access Denied" });
                    auth.signOut();
                }
            }
        } catch (error) {
            Toast.fire({ icon: "error", title: "Invalid credentials!" });
        } finally {
            setLoading(false);
        }
    };

    const textFieldStyles = {
        mb: 2.5,
        '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            backgroundColor: 'rgba(222, 226, 177, 0.05)',
            borderRadius: '12px',
            transition: 'all 0.3s ease',
            '& fieldset': {
                border: `1px solid rgba(222, 226, 177, 0.2)`, 
            },
            '&:hover': {
                backgroundColor: 'rgba(222, 226, 177, 0.08)',
                '& fieldset': {
                    borderColor: 'rgba(222, 226, 177, 0.4)',
                }
            },
            '&.Mui-focused': {
                backgroundColor: 'rgba(255, 213, 0, 0.05)',
                '& fieldset': {
                    border: `1px solid ${colors.gold}`,
                },
                boxShadow: `0 0 0 4px rgba(255, 213, 0, 0.1)`
            },
        },
        '& .MuiInputLabel-root': {
            color: colors.vanilla,
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: colors.gold,
        },
    };

    return (
        <Box sx={{
            minHeight: "100vh",
            backgroundColor: colors.darkGreen,
            backgroundImage: `radial-gradient(circle at 50% 0%, ${colors.darkGreen} 0%, ${colors.calPolyGreen} 80%)`,
            fontFamily: "'Inter', sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2
        }}>

            <Fade in={true} timeout={800}>
                <Paper
                    elevation={24}
                    sx={{
                        width: '100%',
                        maxWidth: 420,
                        borderRadius: '24px',
                        backgroundColor: 'rgba(6, 54, 45, 0.85)', 
                        backdropFilter: 'blur(16px)',
                        border: '1px solid rgba(222, 226, 177, 0.15)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                        p: { xs: 4, md: 5 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}
                >
                    {/* Centered Logo & Brand Name */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                        <img 
                            src="/logo.png" 
                            alt="HotelZio Logo" 
                            style={{ 
                                width: '60px', 
                                marginBottom: '16px',
                                filter: `drop-shadow(0px 4px 8px rgba(255, 213, 0, 0.3))`
                            }} 
                        />
                        <Typography sx={{ 
                            fontSize: 28, 
                            fontWeight: 800, 
                            color: colors.gold, 
                            letterSpacing: "1.5px",
                            textTransform: 'uppercase'
                        }}>
                            HotelZio
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.vanilla, opacity: 0.7, mt: 1 }}>
                            Sign in to your manager dashboard
                        </Typography>
                    </Box>

                    {/* Input Fields */}
                    <Box sx={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Email address"
                            sx={textFieldStyles}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailOutlined sx={{ color: colors.vanilla, ml: 0.5 }} fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Password"
                            type={showPassword ? "text" : "password"}
                            sx={textFieldStyles}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') login1();
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlined sx={{ color: colors.vanilla, ml: 0.5 }} fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                            sx={{ color: colors.vanilla, mr: 0.5 }}
                                        >
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box display="flex" justifyContent="flex-end" mb={3}>
                            <Typography sx={{
                                fontSize: "13px",
                                color: colors.vanilla,
                                cursor: "pointer",
                                fontWeight: 500,
                                opacity: 0.8,
                                "&:hover": { color: colors.gold, opacity: 1 },
                                transition: "all 0.2s"
                            }}>
                                Forgot Password?
                            </Typography>
                        </Box>

                        {/* Submit Button */}
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={login1}
                            disabled={loading}
                            sx={{
                                py: 1.8,
                                background: colors.gold,
                                color: colors.darkGreen,
                                fontWeight: 800,
                                fontSize: "16px",
                                borderRadius: "12px",
                                textTransform: "none",
                                boxShadow: `0 8px 20px rgba(255, 213, 0, 0.2)`,
                                "&:hover": {
                                    background: colors.goldenrod,
                                    boxShadow: `0 10px 25px rgba(209, 165, 5, 0.35)`,
                                    transform: "translateY(-1px)"
                                },
                                transition: 'all 0.3s ease',
                                "&.Mui-disabled": {
                                    background: "rgba(255, 213, 0, 0.3)",
                                    color: "rgba(6, 54, 45, 0.5)"
                                }
                            }}
                        >
                            {loading ? <CircularProgress size={24} sx={{ color: colors.darkGreen }} /> : "Sign In"}
                        </Button>
                    </Box>
                </Paper>
            </Fade>
        </Box>
    );
}

export default Login;